/**
 * <gift-with-purchase> — automatic Gift With Purchase (GWP).
 *
 * Owns the *automatic / threshold* flow in the cart: it adds a complimentary
 * gift line once the qualifying subtotal reaches the configured threshold,
 * removes it when the cart drops back below, and respects a manual removal
 * until the cart re-crosses the threshold. Pricing the gift to $0 is handled
 * by the `gwp-discount` Shopify function — the theme is only the source of
 * truth for *whether the gift line is in the cart*.
 *
 * The element is re-rendered (and therefore re-`connectedCallback`-ed) by the
 * existing cart section-render flow on every cart mutation, so each connect is
 * a fresh re-evaluation against the server cart. State that must survive those
 * re-renders (the in-flight lock, the runtime out-of-stock flag, the one-time
 * removal listener) lives at module scope.
 *
 * Config is read in Liquid from the `gwp` block of the
 * `custom.checkout-settings` shop metafield and passed in as data-attributes.
 */

// Module-scoped guards: the element instance is destroyed/recreated on every
// section re-render, so per-instance flags would not persist.
let busy = false
let outOfStockThisSession = false
let removalListenerInstalled = false

function postJSON(url: string, body: unknown): Promise<Response> {
  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  })
}

function refreshCart(): void {
  // Reuse the theme's section-render flow so drawer, cart page and bubble all
  // stay in sync rather than hand-rolling DOM updates.
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:refresh", { bubbles: true })
  )
}

function setSuppressed(value: boolean): Promise<Response> {
  // An empty string clears the attribute; "true" persists the manual removal
  // across page loads until the cart drops below the threshold again.
  return postJSON(`${window.theme.routes?.cart_update_url}.js`, {
    attributes: { gwp_suppressed: value ? "true" : "" },
  })
}

/**
 * Capture-phase listener that intercepts the remove click on the gift line
 * before the theme's own `.remove` handler runs. We persist suppression first,
 * then remove the line, in one ordered sequence — avoiding a race between two
 * independent cart writes. Installed once; reads everything it needs from the
 * trigger's dataset so it is independent of the element's lifecycle.
 */
function installRemovalListener(): void {
  if (removalListenerInstalled) return
  removalListenerInstalled = true

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target as HTMLElement | null
      const trigger = target?.closest<HTMLElement>("[data-gwp-remove]")
      if (!trigger) return

      event.preventDefault()
      event.stopImmediatePropagation()
      if (busy) return

      busy = true
      const key = trigger.dataset.gwpKey || ""
      setSuppressed(true)
        .then(() =>
          postJSON(window.theme.routes?.cart_change_url || "", {
            id: key,
            quantity: 0,
          })
        )
        .then(() => refreshCart())
        .catch(() => {})
        .finally(() => {
          busy = false
        })
    },
    true
  )
}

type GwpData = {
  enabled: boolean
  threshold: number // shop-currency cents
  cartTotal: number // presentment cents
  giftLinePrice: number // presentment cents
  giftInCart: boolean
  giftKey: string
  giftVariantId: string
  giftAvailable: boolean
  suppressed: boolean
}

class GiftWithPurchase extends HTMLElement {
  connectedCallback(): void {
    installRemovalListener()
    this.render()
    this.evaluate()
  }

  private get data(): GwpData {
    const d = this.dataset
    return {
      enabled: d.enabled === "true",
      threshold: parseInt(d.threshold || "0", 10),
      cartTotal: parseInt(d.cartTotal || "0", 10),
      giftLinePrice: parseInt(d.giftLinePrice || "0", 10),
      giftInCart: d.giftInCart === "true",
      giftKey: d.giftKey || "",
      giftVariantId: d.giftVariantId || "",
      giftAvailable: d.giftAvailable === "true",
      suppressed: d.suppressed === "true",
    }
  }

  /**
   * The threshold is stored in the shop (AU) currency; convert it to the active
   * presentment currency so the comparison against `cart.total_price` is
   * apples-to-apples. Mirrors the conversion in `free-shipping.js`.
   */
  private get threshold(): number {
    const shopify = window.Shopify as unknown as {
      currency?: { rate?: number | string }
    }
    const rate = shopify?.currency?.rate
      ? parseFloat(String(shopify.currency.rate))
      : 1
    return Math.round(this.data.threshold * (rate || 1))
  }

  /** The gift never counts toward its own threshold. */
  private qualifyingSubtotal(): number {
    return this.data.cartTotal - this.data.giftLinePrice
  }

  private render(): void {
    // The banner only confirms the gift has been added. The "add X more" and
    // out-of-stock messaging lives on the gift line, rendered in Liquid and
    // refreshed via the cart section-render flow, so there is nothing to
    // compute here beyond banner visibility.
    this.hidden = !this.data.enabled || !this.data.giftInCart
  }

  private evaluate(): void {
    if (busy) return

    const {
      enabled,
      giftInCart,
      giftAvailable,
      giftVariantId,
      giftKey,
      suppressed,
    } = this.data
    if (!enabled) return

    const unavailable = !giftAvailable || outOfStockThisSession
    const qualifies = this.qualifyingSubtotal() >= this.threshold

    if (qualifies) {
      if (!giftInCart && !suppressed && !unavailable && giftVariantId) {
        this.addGift(giftVariantId)
      }
    } else if (giftInCart && giftKey) {
      // Dropped below the threshold — withdraw the gift.
      this.removeGift(giftKey)
    } else if (suppressed) {
      // Re-arm: dropping below the threshold clears a manual suppression so
      // re-crossing it adds the gift again.
      setSuppressed(false)
    }
  }

  private addGift(variantId: string): void {
    busy = true
    postJSON(window.theme.routes?.cart_add_url || "", {
      id: variantId,
      quantity: 1,
      properties: { _gwp: "true" },
    })
      .then((res) => {
        if (res.status === 422) {
          // Stock ran out between render and add — do not retry this session.
          // The unavailable messaging is driven server-side from the variant's
          // availability, so there is nothing to toggle here.
          outOfStockThisSession = true
          return
        }
        if (res.ok) refreshCart()
      })
      .catch(() => {})
      .finally(() => {
        busy = false
      })
  }

  private removeGift(key: string): void {
    busy = true
    postJSON(window.theme.routes?.cart_change_url || "", {
      id: key,
      quantity: 0,
    })
      .then(() => refreshCart())
      .catch(() => {})
      .finally(() => {
        busy = false
      })
  }
}

if (!customElements.get("gift-with-purchase")) {
  customElements.define("gift-with-purchase", GiftWithPurchase)
}
